import { useEffect, useRef, useState } from "react";
import { Home, X, Share } from "lucide-react";

// Disappearing "Add to Home Screen" flyer.
// - Only shows on the /shop route (the natural landing page).
// - Never bothers the user again for 7 days after dismissal (localStorage flag).
// - Skips itself when the app is already installed (display-mode: standalone / iOS standalone).
// - Auto-dismisses after ~18 seconds so it never lingers.
const LS_KEY = "a2h-hide-until";
const HIDE_DAYS = 7;

const isStandalone = () => {
  if (typeof window === "undefined") return false;
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  return !!window.navigator.standalone;
};

const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iOSLike = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const macTouch = ua.includes("Mac") && "ontouchend" in document;
  return iOSLike || macTouch;
};

const AddToHomeFlyer = () => {
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const deferredPromptRef = useRef(null);

  useEffect(() => {
    // Skip if user hid it recently or app is already installed.
    if (isStandalone()) return;
    try {
      const until = Number(localStorage.getItem(LS_KEY) || 0);
      if (until && Date.now() < until) return;
    } catch {
      /* ignore */
    }
    // Capture the native prompt if the browser offers it (Chrome/Edge/Android).
    const handler = (e) => {
      e.preventDefault();
      deferredPromptRef.current = e;
    };
    window.addEventListener("beforeinstallprompt", handler);
    // Show after a short delay so it doesn't block first paint.
    const showTimer = setTimeout(() => setVisible(true), 2500);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      clearTimeout(showTimer);
    };
  }, []);

  // Auto-disappear after ~18s of showing.
  useEffect(() => {
    if (!visible) return;
    const t = setTimeout(() => dismiss(false), 18000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const dismiss = (persist) => {
    setLeaving(true);
    setTimeout(() => setVisible(false), 320);
    if (persist) {
      try {
        localStorage.setItem(
          LS_KEY,
          String(Date.now() + HIDE_DAYS * 24 * 60 * 60 * 1000)
        );
      } catch {
        /* ignore */
      }
    }
  };

  const install = async () => {
    const p = deferredPromptRef.current;
    if (!p) return; // iOS path shows manual instructions only.
    try {
      p.prompt();
      const choice = await p.userChoice;
      deferredPromptRef.current = null;
      dismiss(choice.outcome === "accepted");
    } catch {
      dismiss(false);
    }
  };

  if (!visible) return null;
  const nativeAvailable = !!deferredPromptRef.current;
  const ios = isIOS();

  return (
    <div
      className={`a2h-flyer${leaving ? " a2h-leaving" : ""}`}
      data-testid="a2h-flyer"
      role="dialog"
      aria-labelledby="a2h-title"
    >
      <button
        type="button"
        className="a2h-close"
        onClick={() => dismiss(true)}
        aria-label="Dismiss for a week"
        data-testid="a2h-close"
      >
        <X size={16} strokeWidth={3} />
      </button>
      <div className="a2h-body">
        <div className="a2h-icon" aria-hidden="true">
          <span className="text-3xl">☕</span>
        </div>
        <div className="flex-1 min-w-0">
          <div id="a2h-title" className="font-display text-base sm:text-lg leading-tight">
            Add 67Cafe to your home screen
          </div>
          <p className="text-xs sm:text-sm mt-0.5" style={{ color: "#484848" }}>
            {ios ? (
              <>
                Tap <Share size={12} strokeWidth={3} className="inline mx-0.5" />{" "}
                <b>Share</b> then <b>Add to Home Screen</b> for one-tap play.
              </>
            ) : nativeAvailable ? (
              <>Tap Install for a full-screen, offline-friendly hangout.</>
            ) : (
              <>Bookmark 67Cafe on your home screen — one tap to play any time.</>
            )}
          </p>
        </div>
        {nativeAvailable ? (
          <button
            type="button"
            className="btn-3d btn-sm btn-purple"
            onClick={install}
            data-testid="a2h-install"
          >
            <Home size={14} strokeWidth={3} />
            Install
          </button>
        ) : (
          <button
            type="button"
            className="btn-3d btn-sm btn-purple"
            onClick={() => dismiss(true)}
            data-testid="a2h-got-it"
          >
            Got it!
          </button>
        )}
      </div>
    </div>
  );
};

export default AddToHomeFlyer;
